"use client";

import { useRef } from "react";
import { motion, useInView, useReducedMotion } from "motion/react";

/* Mindtale doodles: small hand-drawn marks (24px grid, round caps) that ink
   themselves once when they scroll into view. Used as "stickers" on cards and
   photos and as accents next to headings, in the same line language as the
   larger <LineDrawing> scenes.

   A part with `fill` also paints the page ground behind its stroke so the
   doodle reads cleanly where it overlaps a photo or a button edge. */

type Part = { d: string; tone?: "ink" | "accent" | "warm"; fill?: boolean; dots?: boolean };

export const DOODLES = {
  heart: [{ d: "M12 20 s-7 -4.2 -7 -9.4 a3.6 3.6 0 0 1 7 -1.4 a3.6 3.6 0 0 1 7 1.4 c0 5.2 -7 9.4 -7 9.4 z", tone: "warm", fill: true }],
  chat: [
    { d: "M4 5 h16 a2.5 2.5 0 0 1 2.5 2.5 v7 a2.5 2.5 0 0 1 -2.5 2.5 h-9 l-4.5 4 v-4 h-2.5 a2.5 2.5 0 0 1 -2.5 -2.5 v-7 a2.5 2.5 0 0 1 2.5 -2.5 z", fill: true },
    { d: "M8 11 h.01 M12 11 h.01 M16 11 h.01", tone: "accent", dots: true },
  ],
  leaf: [
    { d: "M5 19 C 5 10, 11 4, 20 4 C 20 13, 14 19, 5 19 z", tone: "accent", fill: true },
    { d: "M5 19 L 14 10", tone: "accent" },
  ],
  sparkle: [{ d: "M12 3 c.6 4.4 2.6 6.4 7 7 -4.4 .6 -6.4 2.6 -7 7 -.6 -4.4 -2.6 -6.4 -7 -7 4.4 -.6 6.4 -2.6 7 -7 z", tone: "warm", fill: true }],
  moon: [{ d: "M15 3 a9 9 0 1 0 6 13 a7 7 0 0 1 -6 -13 z", fill: true }],
  plant: [
    { d: "M7 21 l-1.5 -6 h13 l-1.5 6 z", fill: true },
    { d: "M12 15 q-5 -5 -2 -11 M12 15 q3 -6 8 -7", tone: "accent" },
  ],
  checklist: [
    { d: "M5 2.5 h14 a2 2 0 0 1 2 2 v15 a2 2 0 0 1 -2 2 h-14 a2 2 0 0 1 -2 -2 v-15 a2 2 0 0 1 2 -2 z", fill: true },
    { d: "M6.5 8.5 l1.6 1.6 3 -3 M6.5 15 l1.6 1.6 3 -3", tone: "accent" },
    { d: "M13.5 8.5 h4.5 M13.5 15 h4.5" },
  ],
  book: [
    { d: "M12 6.5 c-2 -1.6 -4.8 -2.2 -9 -2 v13 c4.2 -.2 7 .4 9 2 2 -1.6 4.8 -2.2 9 -2 v-13 c-4.2 -.2 -7 .4 -9 2 z", fill: true },
    { d: "M12 6.5 v13", tone: "accent" },
  ],
  code: [{ d: "M8 7 l-5 5 5 5 M16 7 l5 5 -5 5 M13.5 5 l-3 14", tone: "accent" }],
  coffee: [
    { d: "M4 10 h13 v5 a5 5 0 0 1 -5 5 h-3 a5 5 0 0 1 -5 -5 z", fill: true },
    { d: "M17 12 h1.5 a2.5 2.5 0 0 1 0 5 h-1.5" },
    { d: "M8 7 q-2 -2 0 -4 M12.5 7 q-2 -2 0 -4", tone: "warm" },
  ],
  star: [{ d: "M12 3 l2.6 5.6 6 .7 -4.5 4.1 1.2 6 -5.3 -3 -5.3 3 1.2 -6 -4.5 -4.1 6 -.7 z", tone: "warm", fill: true }],
  bulb: [
    { d: "M9 17 h6 M10 21 h4 M8.5 14 a6 6 0 1 1 7 0 c-.8 .6 -1.2 1.6 -1.2 3 h-4.6 c0 -1.4 -.4 -2.4 -1.2 -3 z", fill: true },
    { d: "M3 9 h-1.5 M22.5 9 H21 M5 3 l1 1 M19 3 l-1 1", tone: "warm" },
  ],
  question: [{ d: "M8.5 8.5 a3.5 3.5 0 1 1 5 3.2 c-1 .5 -1.5 1.3 -1.5 2.3 v1 M12 19.5 h.01", tone: "accent" }],
  compass: [
    { d: "M3 12 a9 9 0 1 0 18 0 a9 9 0 1 0 -18 0", fill: true },
    { d: "M15.5 8.5 l-2 5 -5 2 2 -5 z", tone: "warm" },
  ],
  paw: [
    { d: "M7.9 16.2 c.9 -2.5 2.4 -3.9 4.1 -3.9 s3.2 1.4 4.1 3.9 c.7 2 -.5 3.8 -2.4 3.8 -.7 0 -1.1 -.35 -1.7 -.35 s-1 .35 -1.7 .35 c-1.9 0 -3.1 -1.8 -2.4 -3.8 z", fill: true },
    { d: "M5.6 9.4 v.01 M9.2 5 v.01 M14.8 5 v.01 M18.4 9.4 v.01", tone: "warm", dots: true },
  ],
  rays: [{ d: "M16 23 l-8 -1 M18.5 18.5 l-7 -7 M23 16 l-1 -8", tone: "warm" }],
  mail: [
    { d: "M3 6 h18 v12 h-18 z", fill: true },
    { d: "M3.5 6.5 l8.5 6.5 8.5 -6.5", tone: "accent" },
  ],
  bars: [
    { d: "M3 20.5 h18" },
    { d: "M6 17 v-5 M11 17 v-10 M16 17 v-7", tone: "accent" },
    { d: "M19.5 4 l-2.5 2.5", tone: "warm" },
  ],
  receipt: [
    { d: "M6 3 h12 v18 l-2 -1.5 -2 1.5 -2 -1.5 -2 1.5 -2 -1.5 -2 1.5 z", fill: true },
    { d: "M9 8 h6 M9 12 h6", tone: "accent" },
    { d: "M9 16 h3", tone: "warm" },
  ],
  shield: [
    { d: "M12 3 l7 3 v5 c0 5 -3 8 -7 10 c-4 -2 -7 -5 -7 -10 v-5 z", fill: true },
    { d: "M9 12 l2 2 4 -4", tone: "accent" },
  ],
  folder: [
    { d: "M3 6 h6 l2 2 h10 v11 h-18 z", fill: true },
    { d: "M7 13 h10", tone: "accent" },
  ],
} satisfies Record<string, Part[]>;

export type DoodleName = keyof typeof DOODLES;

export type DoodlePalette = { ink: string; accent: string; warm: string; ground: string };
export const DOODLE_LIGHT: DoodlePalette = { ink: "#264653", accent: "#2C969B", warm: "#DC7050", ground: "#F8F8F7" };
export const DOODLE_DARK: DoodlePalette = { ink: "#FFFFFF", accent: "#6FBFC3", warm: "#DC7050", ground: "#264653" };

export default function Doodle({
  name,
  className = "size-7",
  palette = DOODLE_LIGHT,
  delay = 0.2,
  strokeWidth = 1.8,
  play,
}: {
  name: DoodleName;
  /** Size and position (e.g. "absolute -right-3 -top-3 size-8"). */
  className?: string;
  palette?: DoodlePalette;
  delay?: number;
  strokeWidth?: number;
  /** Drive it from outside; by default it plays when scrolled into view. */
  play?: boolean;
}) {
  const ref = useRef<SVGSVGElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.8 });
  const still = useReducedMotion() ?? false;
  const go = (play ?? inView) || still;

  return (
    <svg
      ref={ref}
      aria-hidden
      viewBox="0 0 24 24"
      className={`pointer-events-none overflow-visible ${className}`}
    >
      {(DOODLES[name] as readonly Part[]).map((part, i) => {
        const at = still ? 0 : delay + i * 0.22;
        const color = palette[part.tone ?? "ink"];
        return (
          <g key={i}>
            {part.fill && (
              <motion.path
                d={part.d}
                fill={palette.ground}
                initial={still ? false : { opacity: 0 }}
                animate={go ? { opacity: 1 } : undefined}
                transition={{ delay: at, duration: 0.2 }}
              />
            )}
            <motion.path
              d={part.d}
              fill="none"
              stroke={color}
              strokeWidth={part.dots ? strokeWidth * 1.9 : strokeWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={still ? false : { pathLength: 0, opacity: 0 }}
              animate={go ? { pathLength: 1, opacity: 1 } : undefined}
              transition={{
                pathLength: { duration: still ? 0 : 0.6, delay: at, ease: [0.45, 0, 0.2, 1] },
                opacity: { duration: 0.01, delay: at },
              }}
            />
          </g>
        );
      })}
    </svg>
  );
}
